import { cn, Command, CommandItem, CommandList, Button } from '@duck/ui'
import { TableCellsIcon } from '@heroicons/react/24/outline'
import { getTableDataFromQuery } from './utils'
import { KeyRound, ChevronLeft, ChevronRight } from 'lucide-react'
import { useEventEmitter } from '@/modules/EventBusModule'

const InlineCode = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <code
      className={cn(
        'bg-muted/60 relative rounded px-[0.3rem] py-[2px] font-mono text-sm',
        className
      )}
    >
      {children}
    </code>
  )
}

interface DatabaseSchemaProps {
  tableQueries: string[];
}

type FieldWithIcon = {
  name: string;
  type: string;
  icon?: React.ReactNode;
};

const SingleTableCard = ({
  name,
  fieldsWithIcon,
  onValueClick,
}: {
  name: string;
  fieldsWithIcon: FieldWithIcon[];
  onValueClick: (value: string) => void;
}) => (
  <div className="border border-border rounded overflow-hidden w-[350px]">
    <div className="flex items-center justify-between bg-secondary/50">
      <div
        className="px-4 py-2 flex items-center gap-2 cursor-pointer"
        onClick={() => onValueClick(name)}
      >
        <TableCellsIcon className="w-4 h-4 text-primary" />
        <span className="text-sm font-semibold text-foreground">{name}</span>
      </div>
    </div>
    <div className="p-3">
      <div className="flex flex-wrap gap-2">
        <Command>
          <CommandList>
            {fieldsWithIcon.map((field) => (
              <CommandItem
                key={field.name}
                className="cursor-pointer"
                onSelect={() => onValueClick(field.name)}
              >
                <span className="min-w-4 me-2">
                  {field.type?.toUpperCase().includes('PRIMARY KEY') ? (
                    <KeyRound className="w-4 h-4 text-primary" />
                  ) : null}
                </span>
                <span className="me-2">
                  <InlineCode>{field.name}</InlineCode>
                </span>
                <span className="whitespace-nowrap text-muted-foreground text-[11px]">
                  {field.type}
                </span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </div>
    </div>
  </div>
)

interface DatabaseSchemaNavigatorProps {
  tableQueries: string[]
  currentTableIndex: number
  onCurrentTableIndexChange: (index: number | ((prev: number) => number)) => void
  /** When set, field/table clicks insert into this handler only (e.g. current tab). Otherwise emits global codeInput:insert. */
  onInsertValue?: (value: string) => void
}

export const DatabaseSchemaNavigator = ({
  tableQueries,
  currentTableIndex,
  onCurrentTableIndexChange,
  onInsertValue,
}: DatabaseSchemaNavigatorProps) => {
  const emitInsert = useEventEmitter('codeInput:insert')
  const count = tableQueries.length
  const clampedIndex = count > 0 ? Math.min(currentTableIndex, count - 1) : 0

  const setCurrentIndex = (updater: (prev: number) => number) => {
    onCurrentTableIndexChange(updater(clampedIndex))
  }

  const handleValueClick = (value: string) => {
    if (onInsertValue) {
      onInsertValue(value)
    } else {
      emitInsert({ value })
    }
  }

  if (count === 0) return null

  const query = tableQueries[clampedIndex]
  const { name, fields } = getTableDataFromQuery(query)
  const fieldsWithIcon: FieldWithIcon[] = fields.map((f) => ({ ...f }))

  return (
    <div className="flex flex-col gap-2 p-1">
      {count > 1 && (
        <div className="flex items-center justify-between gap-2 w-[350px]">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => setCurrentIndex((i) => (i <= 0 ? count - 1 : i - 1))}
            aria-label="Previous table"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground truncate flex-1 text-center">
            {clampedIndex + 1} / {count}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => setCurrentIndex((i) => (i >= count - 1 ? 0 : i + 1))}
            aria-label="Next table"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
      <SingleTableCard
        name={name}
        fieldsWithIcon={fieldsWithIcon}
        onValueClick={handleValueClick}
      />
    </div>
  )
}

export const DatabaseSchema = ({ tableQueries }: DatabaseSchemaProps) => {
  const emitInsert = useEventEmitter('codeInput:insert')

  const handleValueClick = (value: string) => {
    emitInsert({ value })
  }

  return (
    <>
      <div className="space-y-3">
        {tableQueries.map((query, index) => {
          const { name, fields } = getTableDataFromQuery(query)
          const fieldsWithIcon: FieldWithIcon[] = fields.map((field) => ({
            ...field,
          }))
          return (
            <div
              key={index}
              className="border border-border rounded overflow-hidden"
            >
              <div className="flex items-center justify-between bg-secondary/50 ">
                <div
                  className="px-4 py-2 flex items-center gap-2 cursor-pointer"
                  onClick={() => handleValueClick(name)}
                >
                  <TableCellsIcon className="w-4 h-4 text-primary" />
                  <span className="text-sm font-semibold text-foreground">
                    {name}
                  </span>
                </div>
              </div>
              <div className="p-3">
                <div className="flex flex-wrap gap-2">
                  <Command>
                    <CommandList>
                      {fieldsWithIcon.map((field, index) => (
                        <CommandItem
                          key={field.name}
                          className="cursor-pointer"
                          onSelect={() => handleValueClick(field.name)}
                        >
                          <span className="min-w-4 me-2">
                            {field.type
                              ?.toUpperCase()
                              .includes('PRIMARY KEY') ? (
                              <KeyRound className="w-4 h-4 text-primary" />
                            ) : null}
                          </span>
                          <span className="me-2">
                            <InlineCode className="">{field.name}</InlineCode>
                          </span>
                          <span className="whitespace-nowrap text-muted-foreground text-[11px]">
                            {field.type}
                          </span>
                          {/* </Badge> */}
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}
